<template>
  <div class="floors-directory">
    <div class="filters">
      <CustomSelect :options="entrances" label="Подъезд" option-label-key="label" option-value-key="entrance"
        @change="onEntranceChange" :value="entrance" placeholder="Выберите подъезд" :clearable="false" />
      <CustomButton v-if="entrance" color="primary-light" @click="() => callAddFloorModal()"
        style="width: auto; height: 40px;" no-caps>
        <span>Добавить этаж</span>
      </CustomButton>
      <CustomButton v-if="entrance" color="primary" @click="openCopyEntranceToProjectModal()"
        style="width: auto; height: 40px;" no-caps>
        <q-icon name="drive_file_move" size="18px" />
        <span>Перенести весь подъезд в другой проект</span>
      </CustomButton>
    </div>

    <q-linear-progress v-if="isFetching" indeterminate />

    <div v-if="placeholderMessage" class="table-placeholder">
      {{ placeholderMessage }}
    </div>

    <CustomTable v-if="floors.length" :rowsData="floors" :customHeader="true" :custom-body="true" :small="true"
      style="width: 70%;">
      <template #customHeader>
        <q-tr>
          <q-th align="center">Этаж</q-th>
          <q-th align="center">Действие</q-th>
        </q-tr>
      </template>
      <template #customBody="slotProps">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center" style="width: 100px; min-width: 100px;">
            {{ slotProps.row.level_num || '—' }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center" style="width: 100px; min-width: 100px;">
            <div style="display: flex; flex-wrap: wrap; gap: 8px; width: 100%; justify-content: center;">
              <CustomButton color="primary-light" text="Редактировать" @click="() => callEditFloorModal(slotProps.row)"
                style="width: auto; height: 30px; min-height: 30px;" no-caps>
                <q-icon name="edit" size="16px" />
              </CustomButton>
              <CustomButton color="red" text="Удалить" @click="() => deleteFloor(slotProps.row.floor_map_id)"
                style="width: auto; height: 30px; min-height: 30px;" no-caps>
                <q-icon name="delete" size="16px" />
              </CustomButton>
              <CustomButton color="primary" text="Перенести в другой проект" @click="() => openCopyToProjectModal(slotProps.row)"
                style="width: auto; height: 30px; min-height: 30px;" no-caps>
                <q-icon name="drive_file_move" size="16px" />
              </CustomButton>
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <q-dialog v-model="copyEntranceToProjectModalOpen" persistent>
      <q-card style="min-width: 360px;">
        <q-card-section>
          <div class="text-h6">Перенести весь подъезд в другой проект</div>
          <div v-if="entrance" class="text-caption text-grey q-mt-xs">
            Подъезд {{ entrance }} — все этажи будут перенесены
          </div>
        </q-card-section>
        <q-card-section>
          <CustomSelect
            :options="copyEntranceProjectOptions"
            option-value-key="project_id"
            option-label-key="label"
            :value="copyEntranceTargetProjectId"
            @change="(v) => (copyEntranceTargetProjectId = v)"
            placeholder="Выберите новый проект"
            :loading="copyEntranceLoading"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" v-close-popup @click="copyEntranceToProjectModalOpen = false" />
          <q-btn
            unelevated
            label="Перенести подъезд"
            color="primary"
            :loading="copyEntranceLoading"
            :disable="!copyEntranceTargetProjectId"
            @click="submitCopyEntranceToProject"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="copyToProjectModalOpen" persistent>
      <q-card style="min-width: 360px;">
        <q-card-section>
          <div class="text-h6">Перенести этаж в другой проект</div>
          <div v-if="copyToProjectFloorRow" class="text-caption text-grey q-mt-xs">
            Этаж {{ copyToProjectFloorRow.level_num || '—' }} (floor_map_id: {{ copyToProjectFloorRow.floor_map_id }})
          </div>
        </q-card-section>
        <q-card-section>
          <CustomSelect
            :options="copyToProjectProjectOptions"
            option-value-key="project_id"
            option-label-key="label"
            :value="copyToProjectTargetProjectId"
            @change="(v) => (copyToProjectTargetProjectId = v)"
            placeholder="Выберите новый проект"
            :loading="copyToProjectLoading"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" v-close-popup @click="copyToProjectModalOpen = false" />
          <q-btn
            unelevated
            label="Перенести"
            color="primary"
            :loading="copyToProjectLoading"
            :disable="!copyToProjectTargetProjectId"
            @click="submitCopyToProject"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { useAppStore } from '@/services/store/app';
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import { MODAL_NAMES } from '@/constants/index';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import CustomSelect from '@/components/form/CustomSelect';
import { computed, ref, watch } from 'vue';
import { getProjectEntranceFloors, getProjectEntrances, createProjectFloor, deleteProjectFloor, getProjectsForTransfer, copyFloorToProject, transferEntranceProject } from "../ProjectsEntry/services";
import CustomButton from '@/components/common/CustomButton';

const props = defineProps({
  presetKitId: {
    type: Number,
    required: false
  },
  title: {
    type: String,
    default: ''
  },
  projectId: {
    type: Number,
  },
  mode: {
    type: String,
  },
  projectData: {
    type: Object,
    default: null
  }
})

const { showModal, closeModal } = useAppStore()
const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()

const entrances = ref([])
const floors = ref([])
const entrance = ref(null)
const status = ref(null)
const isFetching = ref(false)
const loading = ref(false)

const copyToProjectModalOpen = ref(false)
const copyToProjectTargetProjectId = ref(null)
const copyToProjectProjects = ref([])
const copyToProjectLoading = ref(false)
const copyToProjectFloorRow = ref(null)

const copyEntranceToProjectModalOpen = ref(false)
const copyEntranceTargetProjectId = ref(null)
const copyEntranceProjects = ref([])
const copyEntranceLoading = ref(false)

const placeholderMessage = computed(() => {
  if (isFetching.value) return ''
  if (!entrance.value) return 'Выберите подъезд, чтобы увидеть этажи'
  if (!floors.value.length) return 'В выбранном подъезде нет этажей'
  return ''
})

const selectedEntrance = computed(() => {
  return entrances.value.find(e => e.entrance === entrance.value)
})

const copyToProjectProjectOptions = computed(() => {
  const currentId = props.projectId
  return (copyToProjectProjects.value || [])
    .filter((p) => p.project_id != null && Number(p.project_id) !== Number(currentId))
    .map((p) => ({ ...p, label: p.project_name || `Проект ${p.project_id}` }))
})

const copyEntranceProjectOptions = computed(() => {
  const currentId = props.projectId
  return (copyEntranceProjects.value || [])
    .filter((p) => p.project_id != null && Number(p.project_id) !== Number(currentId))
    .map((p) => ({ ...p, label: p.project_name || `Проект ${p.project_id}` }))
})

const loadEntrances = async () => {
  if (!props.projectId) {
    entrances.value = []
    return
  }
  const res = await getProjectEntrances(props.projectId)
  entrances.value = res || []
}

const loadFloors = async () => {
  if (!props.projectId || !entrance.value) {
    floors.value = []
    return
  }
  isFetching.value = true
  const res = await getProjectEntranceFloors(props.projectId, entrance.value)
  isFetching.value = false
  if (!res) {
    return
  }
  floors.value = res
  status.value = null
}

const callAddFloorModal = () => {
  showModal(MODAL_NAMES.AddProjectFloor, {
    title: 'Добавить этаж',
    body: {
      project_id: props.projectId,
      entrance_id: entrance.value,
      project_entrance_id: selectedEntrance.value.project_entrance_id,
    },
    onSubmit: (body) => addFloor(body),
  })
}

const addFloor = async (body) => {
  loading.value = true
  const res = await createProjectFloor(body)
  loading.value = false
  if (!res) {
    return
  }
  floors.value = res
  showSucceedSnackbar()
  closeModal()
}

const callEditFloorModal = (row) => {
  showModal(MODAL_NAMES.AddProjectFloor, {
    title: 'Редактировать этаж',
    body: {
      ...row,
      project_id: props.projectId,
      entrance_id: entrance.value,
    },
    onSubmit: (body) => editFloor(body),
  })
}

const editFloor = async (body) => {
  loading.value = true
  const res = await createProjectFloor(body)
  loading.value = false
  if (!res) {
    return
  }
  floors.value = res
  showSucceedSnackbar()
  closeModal()
}

const deleteFloor = async (floor_map_id) => {
  const res = await deleteProjectFloor(floor_map_id, {
    project_id: props.projectId,
    entrance_id: entrance.value,
  })
  if (!res) {
    return
  }
  floors.value = res
  showSucceedSnackbar()
}

const onEntranceChange = (value) => {
  entrance.value = value
  loadFloors()
}

const openCopyEntranceToProjectModal = async () => {
  const pe = selectedEntrance.value?.project_entrance_id
  if (!pe) {
    showErrorSnackbar('Выберите подъезд')
    return
  }
  copyEntranceToProjectModalOpen.value = true
  copyEntranceTargetProjectId.value = null
  const resident_id = props.projectData?.resident_id
  const project_type_id = props.projectData?.project_type_id
  if (!resident_id || !project_type_id) {
    copyEntranceProjects.value = []
    showErrorSnackbar('Нет данных о ЖК/типе проекта для выбора нового проекта')
    return
  }
  copyEntranceLoading.value = true
  try {
    const list = await getProjectsForTransfer(props.projectId, { resident_id, project_type_id })
    copyEntranceProjects.value = Array.isArray(list) ? list : []
  } catch (e) {
    copyEntranceProjects.value = []
  }
  copyEntranceLoading.value = false
}

const submitCopyEntranceToProject = async () => {
  const pe = selectedEntrance.value?.project_entrance_id
  if (!pe || !copyEntranceTargetProjectId.value) return
  copyEntranceLoading.value = true
  try {
    await transferEntranceProject(
      {
        project_entrance_id_from: pe,
        project_id_from: props.projectId,
        project_id_to: copyEntranceTargetProjectId.value,
      },
      {}
    )
    showSucceedSnackbar('Подъезд перенесён в выбранный проект.')
    copyEntranceToProjectModalOpen.value = false
    loadEntrances()
    loadFloors()
  } catch (e) {
    showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка при переносе подъезда')
  }
  copyEntranceLoading.value = false
}

const openCopyToProjectModal = async (row) => {
  copyToProjectFloorRow.value = row
  copyToProjectModalOpen.value = true
  copyToProjectTargetProjectId.value = null
  const resident_id = props.projectData?.resident_id
  const project_type_id = props.projectData?.project_type_id
  if (!resident_id || !project_type_id) {
    copyToProjectProjects.value = []
    showErrorSnackbar('Нет данных о ЖК/типе проекта для выбора нового проекта')
    return
  }
  copyToProjectLoading.value = true
  try {
    const list = await getProjectsForTransfer(props.projectId, { resident_id, project_type_id })
    copyToProjectProjects.value = Array.isArray(list) ? list : []
  } catch (e) {
    copyToProjectProjects.value = []
  }
  copyToProjectLoading.value = false
}

const submitCopyToProject = async () => {
  const row = copyToProjectFloorRow.value
  if (!row?.floor_map_id || !copyToProjectTargetProjectId.value) return
  copyToProjectLoading.value = true
  try {
    await copyFloorToProject(row.floor_map_id, { target_project_id: copyToProjectTargetProjectId.value })
    showSucceedSnackbar('Запрос на перенос этажа выполнен. После подключения функции в БД перенос будет выполняться.')
    copyToProjectModalOpen.value = false
  } catch (e) {
    showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка при переносе этажа')
  }
  copyToProjectLoading.value = false
}

watch(
  () => props.projectId,
  () => {
    entrance.value = null
    status.value = null
    floors.value = []
    loadEntrances()
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.floors-directory {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}

.filters {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: end;
}

.table-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  color: #808080;
  font-size: 14px;
}

.actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.sizes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #3f3f3f;
}

.comments-cell {
  max-width: 280px;
  white-space: normal;
}
</style>